import React, { useState } from 'react';
import { oneOfType, object, bool, func, shape, string, node } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  Form,
  NamedLink,
  PrimaryButtonInline,
  PrimaryButton,
  InlineTextButton,
  PreviewListingButton,
  InfoTooltip,
  Icon,
} from '../../components';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import EditLiveRoomMembershipsField from './EditLiveRoomMembershipsField';
import css from './EditLiveRoomMembershipForm.module.css';

const StripeRequirementsMissing = props => {
  const { isMembershipView, payoutEnabled, onOpenPayoutDetailsModal } = props;
  return !isMembershipView && !payoutEnabled ? (
    <div className={css.stripeRequirementsMissing}>
      <p className={css.stripeRequirementsMissingMessage}>
        <FormattedMessage id="EditLiveRoomMembershipForm.verificationNeededText" />
      </p>
      <PrimaryButton
        type="button"
        className={css.getVerifiedButton}
        onClick={onOpenPayoutDetailsModal}
      >
        <FormattedMessage id="EditLiveRoomMembershipForm.getVerifiedButton" />
      </PrimaryButton>
    </div>
  ) : null;
};

const EditLiveRoomMembershipFormComponent = props => {
  // Keep track of active memberships which allows user to
  // edit single membership.
  const [activeMembershipIndex, setActiveMembershipIndex] = useState(null);
  const [formUpdated, setFormUpdated] = useState(false);

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          className,
          disabled,
          ready,
          handleSubmit,
          invalid,
          pristine,
          form,
          values,
          saveActionMsg,
          updated,
          updateInProgress,
          fetchErrors,
          panelTitle,
          listing,
          isCurrentUserAuthor,
          payoutEnabled,
          availabilityExceptions,
          onOpenPayoutDetailsModal,
          onGetPrevPathParams,
          intl,
        } = formRenderProps;

        const { push } = form.mutators;
        const membershipsFromValues = values.memberships || [];

        const prevPathParams = onGetPrevPathParams();

        const { updateListingError, showListingsError } = fetchErrors || {};
        const errorMessageUpdateListing = updateListingError ? (
          <p className={css.error}>
            <FormattedMessage id="EditLiveRoomMembershipForm.updateFailed" />
          </p>
        ) : null;

        const errorMessageShowListing = showListingsError ? (
          <p className={css.error}>
            <FormattedMessage id="EditLiveRoomMembershipForm.showListingFailed" />
          </p>
        ) : null;

        const membershipTooltipItems = [
          intl.formatMessage({ id: 'EditExperienceMembershipsForm.addingMembershipsHelper1' }),
          intl.formatMessage({ id: 'EditExperienceMembershipsForm.addingMembershipsHelper2' }),
        ];

        const isMembershipView = activeMembershipIndex !== null;

        const classes = classNames(css.root, className);
        const submitReady = (updated && pristine) || ready || formUpdated;
        const submitInProgress = updateInProgress;
        const submitDisabled = invalid || disabled || submitInProgress || isMembershipView;

        return (
          <Form
            className={classes}
            onSubmit={async values => {
              setFormUpdated(false);
              await handleSubmit(values);

              // Notify the component that the form
              // has been updated
              setFormUpdated(true);
            }}
          >
            <div className={css.content}>
              <div className={css.contentWrapper}>
                <h1 className={css.title}>
                  {panelTitle}
                  {!isMembershipView ? <InfoTooltip items={membershipTooltipItems} /> : null}
                </h1>
                <p className={css.subTitle}>
                  <FormattedMessage id="EditLiveRoomMembershipForm.subTitle" />
                </p>

                {isCurrentUserAuthor ? (
                  <StripeRequirementsMissing
                    isMembershipView={isMembershipView}
                    payoutEnabled={payoutEnabled}
                    onOpenPayoutDetailsModal={onOpenPayoutDetailsModal}
                  />
                ) : null}

                <div className={css.membershipsWrapper}>
                  {!isMembershipView ? (
                    <PrimaryButton
                      type="button"
                      className={css.addMembershipButton}
                      onClick={() => {
                        push('memberships', {
                          perks: ['members-only-live-event'],
                        });
                        setActiveMembershipIndex(membershipsFromValues.length);
                      }}
                    >
                      <FormattedMessage id="EditLiveRoomMembershipForm.addMembership" />
                    </PrimaryButton>
                  ) : (
                    <InlineTextButton
                      type="button"
                      className={css.backToMembershipsButton}
                      onClick={() => setActiveMembershipIndex(null)}
                    >
                      <Icon name="arrowLeft" size={20} />
                      <FormattedMessage id="EditLiveRoomMembershipForm.back" />
                    </InlineTextButton>
                  )}
                  <EditLiveRoomMembershipsField
                    intl={intl}
                    push={push}
                    isMembershipView={isMembershipView}
                    membershipsFromValues={membershipsFromValues}
                    activeMembershipIndex={activeMembershipIndex}
                    setActiveMembershipIndex={setActiveMembershipIndex}
                    availabilityExceptions={availabilityExceptions}
                    form={form}
                  />
                </div>

                {errorMessageUpdateListing}
                {errorMessageShowListing}
              </div>
            </div>

            <div className={css.submitButtonRoot}>
              <NamedLink className={css.backButton} name="EditLiveRoomPage" params={prevPathParams}>
                <FormattedMessage id="EditLiveRoomGalleryForm.backButton" />
              </NamedLink>
              <div className={css.submitAndPreviewButton}>
                <PreviewListingButton listing={listing} />
                <PrimaryButtonInline
                  size="small"
                  checkmarkSize="small"
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                  ready={submitReady}
                >
                  {saveActionMsg}
                </PrimaryButtonInline>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

EditLiveRoomMembershipFormComponent.defaultProps = {
  className: null,
  fetchErrors: null,
  isCurrentUserAuthor: false,
  panelTitle: null,
};

EditLiveRoomMembershipFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  panelTitle: oneOfType([object, node]),
  isCurrentUserAuthor: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditLiveRoomMembershipFormComponent);
